import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Card,
  InputLabel,
  Typography,
  Skeleton,
  TextField,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import useStyles from "./GraphingTool.styles";
import LineChartWithLegendFilter from "../../LineChart/LineChartWithLegendFilter";
import WhatWeDo from "../../whatWeDo/WhatWeDo.component";
import DateRangeDatePickerWithPresets from "../../common/DatePicker/DateRangeDatePickerWithPresets";
import { useGraphingToolContext } from "../../../context/GraphingToolContext";
import MultiSelectOption from "../../common/MultiSelectOption/MultiSelectOption";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const mapTicker = (ticker) => ({
  id: ticker.ticker,
  name: ticker.ticker,
});

const mapField = (field) => ({
  id: field,
  name: field,
});

const GraphingTool = () => {
  const { classes } = useStyles();

  const {
    filtersLoading,
    graphingToolFilters,

    tickers,
    selectedTickers,
    setSelectedTickers,

    fields,
    selectedField,
    setSelectedField,

    setSelectedPresetDateRange,

    selectedStartDate,
    setSelectedStartDate,

    selectedEndDate,
    setSelectedEndDate,

    graphingToolData,
    graphingToolDataLoading,
  } = useGraphingToolContext();

  const [filteredData, setFilteredData] = useState([]);

  const { minStartDate, maxEndDate } = graphingToolFilters || {};

  const mappedTickers = [...(tickers || []).map(mapTicker)];
  const mappedFields = [...(fields || []).map(mapField)];

  const handleFieldChange = (field) => {
    setSelectedField(field);
  };

  useEffect(() => {
    const graphingToolFilteredData = graphingToolData?.tickers?.filter(
      (tickerObj) => selectedTickers.includes(tickerObj.ticker),
    );
    setFilteredData(graphingToolFilteredData || []);
  }, [selectedTickers, graphingToolData]);

  return (
    <div className={classes.page}>
      <Grid container spacing={2}>
        <Card
          style={{
            padding: "16px",
            width: "100%",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <MultiSelectOption
                selectedOptions={selectedTickers}
                setSelectedOptions={setSelectedTickers}
                options={mappedTickers}
                label="Tickers"
                labelId="tickers"
                placeholder={"Select Tickers"}
                dropdownWidth={250}
                isLoading={filtersLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InputLabel id={"field"}>Field</InputLabel>
              <Autocomplete
                options={mappedFields}
                getOptionLabel={(option) => option?.name || ""}
                value={
                  mappedFields.find((item) => item.id === selectedField) || null
                }
                onChange={(_, newValue) => {
                  handleFieldChange(newValue ? newValue.id : null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={filtersLoading}
                ListboxProps={{
                  style: {
                    width: "max-content",
                    minWidth: "100%",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    placeholder="Select Field"
                    size="small"
                  />
                )}
                sx={{ width: 250 }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <InputLabel id={"report-date"}>Report Date Range</InputLabel>
              <DateRangeDatePickerWithPresets
                text="Report Date Range"
                minStartDate={minStartDate}
                maxEndDate={maxEndDate}
                setSelectedPresetDateRange={setSelectedPresetDateRange}
                setSelectedStartDate={setSelectedStartDate}
                setSelectedEndDate={setSelectedEndDate}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                isLoading={filtersLoading}
              />
            </Grid>
          </Grid>
        </Card>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Item>
            {graphingToolDataLoading ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={300}
                animation="pulse"
                style={{ marginBottom: 8 }}
              />
            ) : (!graphingToolDataLoading && !filteredData) ||
              (!graphingToolDataLoading && !filteredData?.length) ? (
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                {!selectedTickers?.length ||
                !selectedField ||
                !selectedStartDate ||
                !selectedEndDate
                  ? "Select the filters to see the appropriate Graphing Tool data."
                  : "No data found for the selected filters."}
              </Typography>
            ) : (
              <LineChartWithLegendFilter filteredData={filteredData} />
            )}
          </Item>
        </Grid>
        <Grid size={{ xs: 12, md: 12 }} sx={{ display: "none" }}>
          <Item>
            <WhatWeDo />
          </Item>
        </Grid>
      </Grid>
    </div>
  );
};

export default GraphingTool;
